import { createFileRoute, Link } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  ArrowLeft,
  Camera,
  Loader2,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  createSiteVisit,
  registerSiteVisitAttachment,
  deleteSiteVisitAttachment,
  updateSiteVisit,
  getSiteVisit,
} from "@/lib/site-visit.functions";
import { useActiveCompany } from "@/hooks/use-active-company";
import { PROJECT_IMAGE_BUCKET, uploadProjectImage } from "@/lib/project-images";

export const Route = createFileRoute("/_authenticated/projects/$id/visit")({
  head: () => ({
    meta: [
      { title: "Befaring | VERQO" },
      { name: "description", content: "Start befaring, ta bilder og registrer bilder og notater." },
    ],
  }),
  component: VisitPage,
});

type Step = "capture" | "notes";

function VisitPage() {
  const { id: projectId } = Route.useParams();
  const qc = useQueryClient();
  const { companyId } = useActiveCompany();


  const createFn = useServerFn(createSiteVisit);
  const attachFn = useServerFn(registerSiteVisitAttachment);
  const deleteAttachFn = useServerFn(deleteSiteVisitAttachment);
  const updateFn = useServerFn(updateSiteVisit);
  const getFn = useServerFn(getSiteVisit);

  const [visitId, setVisitId] = useState<string | null>(null);
  const [step, setStep] = useState<Step>("capture");
  const [note, setNote] = useState("");
  const [wishes, setWishes] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  // Ensure visit exists
  const ensureVisit = async (): Promise<string> => {
    if (visitId) return visitId;
    const r = await createFn({ data: { projectId } });
    setVisitId(r.id);
    return r.id;
  };

  const visitQ = useQuery({
    queryKey: ["site-visit", visitId],
    queryFn: () => getFn({ data: { visitId: visitId! } }),
    enabled: !!visitId,
  });

  const onPick = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      if (!companyId) throw new Error("Ingen aktiv bedrift");
      const vid = await ensureVisit();
      for (const f of Array.from(files)) {
        await uploadProjectImage({
          storage: supabase.storage.from(PROJECT_IMAGE_BUCKET) as never,
          companyId,
          projectId,
          file: f,
          register: async (path) => {
            await attachFn({
              data: {
                visitId: vid,
                storagePath: path,
                mimeType: f.type,
                sizeBytes: f.size,
                caption: f.name,
                kind: "image",
              },
            });
          },
        });
      }
      qc.invalidateQueries({ queryKey: ["site-visit", vid] });
      qc.invalidateQueries({ queryKey: ["project-images", projectId] });
      qc.invalidateQueries({ queryKey: ["project-image-urls", projectId] });
      toast.success(`${files.length} bilde${files.length > 1 ? "r" : ""} lagt til`);
    } catch (e) {

      toast.error(e instanceof Error ? e.message : "Opplasting feilet");
    } finally {
      setUploading(false);
    }
  };

  const deleteAttach = useMutation({
    mutationFn: (attachmentId: string) => deleteAttachFn({ data: { attachmentId } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["site-visit", visitId] }),
  });





  const attachments = visitQ.data?.attachments ?? [];

  return (
    <div className="mx-auto flex min-h-screen max-w-lg flex-col bg-background">
      {/* Header */}
      <header className="sticky top-0 z-10 flex items-center gap-2 border-b border-border bg-background/95 px-3 py-3 backdrop-blur">
        <Button asChild variant="ghost" size="sm">
          <Link to="/projects/$id" params={{ id: projectId }}>
            <ArrowLeft className="mr-1 h-4 w-4" /> Avslutt
          </Link>
        </Button>
        <div className="flex-1 text-center text-sm font-semibold">Befaring</div>
        <div className="w-16 text-right text-xs text-muted-foreground">
          {attachments.length} bilde{attachments.length !== 1 ? "r" : ""}
        </div>
      </header>

      {/* Steps indicator */}
      <div className="flex gap-1 border-b border-border px-3 py-2">
        {(["capture", "notes"] as Step[]).map((s, i) => (
          <button
            key={s}
            onClick={() => setStep(s)}
            className={`h-1.5 flex-1 rounded-full transition-colors ${
              step === s ? "bg-primary" : i < ["capture", "notes"].indexOf(step) ? "bg-primary/40" : "bg-muted"
            }`}
            aria-label={`Steg ${i + 1}`}
          />
        ))}
      </div>

      <main className="flex-1 space-y-4 p-3 pb-32">
        {step === "capture" && (
          <div className="space-y-3">
            <h2 className="text-lg font-semibold">Ta bilder</h2>
            <p className="text-sm text-muted-foreground">
              Ta bilder av det du ser på befaringen. De lagres sammen med notatene dine.
            </p>
            <input
              ref={fileInput}
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              className="hidden"
              onChange={(e) => onPick(e.target.files)}
            />
            <Button
              size="lg"
              className="h-16 w-full text-base"
              onClick={() => fileInput.current?.click()}
              disabled={uploading}
            >
              {uploading ? (
                <Loader2 className="mr-2 h-6 w-6 animate-spin" />
              ) : (
                <Camera className="mr-2 h-6 w-6" />
              )}
              Ta bilde / velg fra galleri
            </Button>

            {attachments.length > 0 && (
              <div className="grid grid-cols-3 gap-2">
                {attachments.map((a) => (
                  <div key={a.id} className="relative aspect-square overflow-hidden rounded-lg border border-border">
                    <img src={a.url} alt={a.caption ?? ""} className="h-full w-full object-cover" />
                    <button
                      onClick={() => deleteAttach.mutate(a.id)}
                      className="absolute right-1 top-1 grid h-7 w-7 place-items-center rounded-full bg-black/60 text-white"
                      aria-label="Slett bilde"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {step === "notes" && (
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-semibold">Kort notat om oppdraget</label>
              <p className="text-xs text-muted-foreground">
                Hva ser du? Hva skal gjøres? Kort og enkelt.
              </p>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="F.eks. Bytte gammelt sikringsskap på loftet, kunden vil ha jordfeilautomater på alle kurser."
                rows={5}
                className="text-base"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold">Kundens ønsker</label>
              <p className="text-xs text-muted-foreground">
                Skriv eller lim inn hva kunden har bedt om.
              </p>
              <Textarea
                value={wishes}
                onChange={(e) => setWishes(e.target.value)}
                placeholder="F.eks. Ønsker smart-belysning i stue og kjøkken, USB-uttak ved sengekant."
                rows={5}
                className="text-base"
              />
            </div>
          </div>
        )}

      </main>

      {/* Sticky footer nav */}
      <footer className="fixed inset-x-0 bottom-0 z-10 mx-auto max-w-lg border-t border-border bg-background/95 p-3 backdrop-blur">
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => {
              const order: Step[] = ["capture", "notes"];
              const i = order.indexOf(step);
              if (i > 0) setStep(order[i - 1]);
            }}
            disabled={step === "capture"}
          >
            Tilbake
          </Button>
          <Button
            className="flex-1"
            onClick={() => {
              const order: Step[] = ["capture", "notes"];
              const i = order.indexOf(step);
              if (step === "notes" && (note || wishes)) {
                ensureVisit()
                  .then((vid) => updateFn({ data: { visitId: vid, note, customerWishes: wishes } }))
                  .catch(() => {});
              }
              if (i < order.length - 1) setStep(order[i + 1]);
            }}
            disabled={step === "notes"}
          >
            Neste
          </Button>
        </div>
      </footer>
    </div>
  );
}

